import { __assign } from "tslib";
import React from "react";
import Chart from "chart.js";
import { addDays, addWeeks, addMonths, addHours, startOfDay, startOfWeek, startOfMonth, startOfHour, } from "date-fns";
import "./date-fns-adapter";
export function addScaleUnit(date, scale, count) {
    switch (scale) {
        case "hour":
            return addHours(date, count);
        case "day":
            return addDays(date, count);
        case "week":
            return addWeeks(date, count);
        case "month":
            return addMonths(date, count);
    }
}
var CustomTooltip = function (props) {
    var model = props.model, chartData = props.chartData, datasetLabels = props.datasetLabels;
    if (model &&
        model.dataPoints &&
        model.dataPoints.length &&
        typeof model.dataPoints[0].index === "number") {
        var index_1 = model.dataPoints[0].index;
        var rows = chartData.map(function (dataset) {
            var item = dataset.data[index_1];
            return {
                date: item.x,
                value: item.y,
            };
        });
        if (rows.length) {
            return React.createElement(props.tooltip, {
                top: model.y,
                left: model.x,
                rows: rows,
                datasetLabels: datasetLabels,
            });
        }
    }
    return null;
};
function truncateDate(date, scale, isoWeekday) {
    switch (scale) {
        case "hour":
            return startOfHour(date);
        case "day":
            return startOfDay(date);
        case "week":
            return startOfWeek(date, { weekStartsOn: isoWeekday ? 1 : 0 });
        case "month":
            return startOfMonth(date);
    }
}
function fillMissingRows(data, scale, from, to, isoWeekday, cumulative) {
    var _a, _b;
    var runningTotal = 0;
    var newData = [];
    var knownDateValues = new Map(data.map(function (row) { return [row.date.valueOf(), row.value]; }));
    var lastDateValue = to.valueOf();
    var date = truncateDate(from, scale, isoWeekday);
    while (date.valueOf() < lastDateValue) {
        runningTotal += (_a = knownDateValues.get(date.valueOf())) !== null && _a !== void 0 ? _a : 0;
        var value = (_b = knownDateValues.get(date.valueOf())) !== null && _b !== void 0 ? _b : 0;
        newData.push({
            x: date,
            y: cumulative ? runningTotal : value,
        });
        date = addScaleUnit(date, scale, 1);
    }
    return newData;
}
export var AreaChart = function (props) {
    var fontFamily = props.fontFamily, scale = props.scale, isoWeekday = props.isoWeekday, from = props.from, to = props.to, data = props.datasets, tooltip = props.tooltip, className = props.className, xTicksCB = props.xTicksCB, xAxisShowTicks = props.xAxisShowTicks, yAxisShowTicks = props.yAxisShowTicks, yAxisPosition = props.yAxisPosition, cumulative = props.cumulative;
    var _a = React.useState(null), canvas = _a[0], setCanvas = _a[1];
    var _b = React.useState(null), tooltipModel = _b[0], setTooltipModel = _b[1];
    var ctx = React.useMemo(function () {
        var _a;
        if (canvas) {
            return (_a = canvas.getContext("2d")) !== null && _a !== void 0 ? _a : undefined;
        }
    }, [canvas]);
    var chartData = React.useMemo(function () {
        return data.map(function (dataset) { return (__assign(__assign({}, dataset), { data: fillMissingRows(dataset.data, scale, from, to, isoWeekday, cumulative) })); });
    }, [scale, data, from, to, isoWeekday, cumulative]);
    React.useEffect(function () {
        if (ctx) {
            var chart_1 = new Chart(ctx, {
                type: "line",
                options: {
                    tooltips: {
                        enabled: false,
                        custom: function (model) {
                            setTooltipModel({
                                model: model,
                                chartData: chartData,
                            });
                        },
                    },
                    legend: {
                        display: false,
                    },
                    animation: {
                        duration: 0,
                    },
                    hover: {
                        animationDuration: 0,
                    },
                    maintainAspectRatio: false,
                    responsiveAnimationDuration: 0,
                    scales: {
                        xAxes: [
                            {
                                type: "time",
                                distribution: "linear",
                                time: {
                                    unit: scale,
                                    isoWeekday: isoWeekday,
                                    stepSize: chartData[0] && chartData[0].data.length <= 12
                                        ? 1
                                        : Math.round((chartData[0].data.length - 1) / 11),
                                },
                                gridLines: {
                                    drawTicks: !!xAxisShowTicks,
                                },
                                ticks: {
                                    maxTicksLimit: 12,
                                    callback: function (value, i, values) {
                                        if (xTicksCB && i === 0) {
                                            xTicksCB(values, chartData);
                                        }
                                        return value;
                                    },
                                    display: !!xAxisShowTicks,
                                    minRotation: 0,
                                    maxRotation: 45,
                                    autoSkipPadding: 10,
                                    source: "auto",
                                    fontFamily: fontFamily,
                                },
                            },
                        ],
                        yAxes: [
                            {
                                stacked: true,
                                ticks: {
                                    beginAtZero: true,
                                    fontFamily: fontFamily,
                                    padding: 8,
                                    maxTicksLimit: 6,
                                    fontSize: 16,
                                    fontColor: "#555555",
                                },
                                gridLines: {
                                    drawTicks: !!yAxisShowTicks,
                                },
                                position: yAxisPosition || "left",
                            },
                        ],
                    },
                },
                data: {
                    datasets: chartData.map(function (dataset) { return ({
                        lineTension: 0.15,
                        spanGaps: false,
                        borderColor: dataset.lineColor,
                        backgroundColor: dataset.areaColor,
                        borderWidth: 2,
                        pointHitRadius: 20,
                        pointRadius: 4,
                        pointHoverRadius: 4,
                        pointBorderWidth: 2,
                        pointHoverBorderWidth: 2,
                        pointBackgroundColor: dataset.pointBgColor,
                        pointHoverBackgroundColor: dataset.pointHoverBgColor,
                        pointHoverBorderColor: dataset.lineColor,
                        pointBorderColor: dataset.lineColor,
                        data: dataset.data,
                    }); }),
                },
            });
            return function () {
                chart_1.destroy();
            };
        }
    }, [chartData, ctx, fontFamily, isoWeekday, scale]);
    var datasetLabels = React.useMemo(function () { return data.map(function (x) { return x.label; }); }, [data]);
    return (React.createElement("div", { className: className },
        React.createElement("canvas", { ref: setCanvas }),
        React.createElement(CustomTooltip, { tooltip: tooltip, model: ((tooltipModel === null || tooltipModel === void 0 ? void 0 : tooltipModel.chartData) === chartData && (tooltipModel === null || tooltipModel === void 0 ? void 0 : tooltipModel.model)) || null, chartData: chartData, datasetLabels: datasetLabels })));
};
